import React, { useEffect, useState } from "react";
import { api, formatRupiah } from "@/lib/api";
import { PageHeader, Section, DataCard, Empty } from "@/components/Shared";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function Laporan() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [report, setReport] = useState(null);
  const [insight, setInsight] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);

  useEffect(() => {
    api.get("/sites").then((r) => {
      setSites(r.data);
      if (r.data[0]) setSiteId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!siteId) return;
    api.get(`/laporan/site/${siteId}?period=${period}`).then((r) => setReport(r.data));
  }, [siteId, period]);

  const runAI = async () => {
    if (!report) return;
    setLoadingAI(true); setInsight("");
    try {
      const ctx = `Site: ${report.site?.name}, Periode: ${period}\n` +
        `- Total tonase: ${report.total_tonase}\n` +
        `- Total BBM: ${report.total_bbm_liter} L (${report.total_bbm_cost})\n` +
        `- Absensi: ${report.absensi_count}\n` +
        `- Records produksi: ${report.produksi.length}\n` +
        `- Records kas: ${report.kas_transactions.length}`;
      const { data } = await api.post("/ai/insight", {
        context: ctx, question: "Analisis performa site ini bulan ini. Berikan insight dan rekomendasi.",
      });
      setInsight(data.insight);
    } catch (err) { toast.error("AI gagal: " + err.message); }
    finally { setLoadingAI(false); }
  };

  return (
    <div>
      <PageHeader
        title="Laporan per Site"
        subtitle="Rekap operasional, produksi, biaya per site & periode"
        testId="laporan-header"
        actions={
          <>
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger data-testid="laporan-site" className="w-56 rounded-none bg-neutral-900 border-neutral-800 h-11"><SelectValue placeholder="Pilih site" /></SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-neutral-800 rounded-none">
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="laporan-period" className="w-40 rounded-none bg-neutral-900 border-neutral-800 h-11" />
            <Button onClick={runAI} disabled={loadingAI || !report} data-testid="laporan-ai-btn" className="rounded-none bg-[#FFD700] hover:bg-[#e6c200] text-black font-bold">
              <Sparkles size={16} className="mr-1" /> {loadingAI ? "..." : "AI"}
            </Button>
          </>
        }
      />
      <Section>
        {!report ? <Empty message="Pilih site untuk melihat laporan" /> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <DataCard label="Total Tonase" value={`${(report.total_tonase || 0).toLocaleString('id-ID')} T`} accent="orange" />
              <DataCard label="BBM Liter" value={`${(report.total_bbm_liter || 0).toLocaleString('id-ID')} L`} />
              <DataCard label="Biaya BBM" value={formatRupiah(report.total_bbm_cost)} accent="yellow" />
              <DataCard label="Absensi" value={report.absensi_count} accent="green" />
            </div>

            {insight && (
              <div className="border border-[#FFD700] bg-[#FFD700]/5 p-5 mb-4" data-testid="laporan-insight">
                <div className="font-mono text-[10px] uppercase text-[#FFD700] mb-2 flex items-center gap-1"><Sparkles size={12} /> AI INSIGHT</div>
                <div className="text-sm whitespace-pre-wrap leading-relaxed">{insight}</div>
              </div>
            )}

            <div className="font-mono text-[10px] uppercase text-neutral-500 mb-2">// PRODUKSI DETAIL</div>
            {report.produksi.length === 0 ? <Empty message="Tidak ada produksi periode ini" /> : (
              <div className="border border-neutral-800 bg-[#141414] mb-4">
                <Table>
                  <TableHeader>
                    <TableRow className="border-neutral-800 hover:bg-transparent">
                      <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tanggal</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Material</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Tonase</TableHead>
                      <TableHead className="font-mono text-[10px] uppercase text-neutral-500">Ritase</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.produksi.map((p) => (
                      <TableRow key={p.id} className="border-neutral-800">
                        <TableCell className="font-mono text-xs">{p.date}</TableCell>
                        <TableCell className="font-mono">{p.material}</TableCell>
                        <TableCell className="font-mono font-bold">{(p.tonase || 0).toLocaleString('id-ID')} T</TableCell>
                        <TableCell className="font-mono">{p.ritase}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}
      </Section>
    </div>
  );
}
